import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import JsonLd from '@/components/feature/JsonLd';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

const BASE_URL = 'https://mitao-video.example.com';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'hot');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryVideos = activeSlug === 'hot' || activeSlug === 'new'
    ? activeSlug === 'hot'
      ? videos.filter((v) => v.trending)
      : [...videos].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    : videos.filter((v) => v.category === activeSlug);

  const handleCategoryClick = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: BASE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${currentCategory.name}视频内容`,
        item: `${BASE_URL}/category/${activeSlug}`,
      },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 蜜桃视频在线`,
    description: currentCategory.description,
    url: `${BASE_URL}/category/${activeSlug}`,
  };

  return (
    <>
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={collectionSchema} />
      <SEOMeta
        title={`${currentCategory.name}视频在线观看`}
        description={`蜜桃视频在线${currentCategory.name}专区，${currentCategory.description}。高清画质，免费在线观看，每日更新。`}
        keywords={`蜜桃视频在线,${currentCategory.name}视频,${currentCategory.name}在线观看,免费${currentCategory.name},高清${currentCategory.name},蜜桃视频${currentCategory.name}`}
        canonicalUrl={`${BASE_URL}/category/${activeSlug}`}
      />

      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[{ label: `${currentCategory.name}视频内容` }]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-600">
              {currentCategory.description}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-300 cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {/* Video Grid */}
          {categoryVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {categoryVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-background-50 text-xs">
                      {video.duration}
                    </div>
                    {video.featured && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-primary-500 text-background-50 text-xs font-medium">
                        精选
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                      {video.description}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {video.views}
                      </span>
                      <span>{video.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}